/**
 * FAQ API Service
 *
 * Client for FAQ management API with multi-tenant and localization support.
 */

import axios, { AxiosInstance } from 'axios';

// ============================================================================
// Types
// ============================================================================

export interface FAQCategory {
  id: string;
  slug: string;
  name: string;  // Already localized by Lambda
  icon: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface FAQ {
  id: string;
  slug: string;
  category_slug: string;
  question: string;  // Already localized and brand-replaced by Lambda
  answer: string;    // Markdown content, brand-replaced
  sort_order: number;
  view_count: number;
}

export interface FAQSearchResult {
  id: string;
  slug: string;
  category_slug: string;
  question: string;
  answer: string;
  relevance: number;
}

// Admin types
export interface LocalizedContent {
  question: string;
  answer: string;
}

export interface AdminFAQ {
  id: string;
  slug: string;
  category_id: string;
  category_slug: string;
  tenant_id: string | null;
  localized_content: Record<string, LocalizedContent>;
  sort_order: number;
  is_published: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
}

export interface AdminCategory {
  id: string;
  slug: string;
  tenant_id: string | null;
  localized_names: Record<string, string>;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  faq_count: number;
  created_at: string;
  updated_at: string;
}

export interface TenantOverride {
  id: string;
  faq_slug: string;
  action: 'override' | 'exclude';
  override_content: Record<string, LocalizedContent> | null;
  original_faq_id: string | null;
  original_content: Record<string, LocalizedContent> | null;
  category_slug: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateFAQRequest {
  category_id: string;
  slug: string;
  localized_content: Record<string, LocalizedContent>;
  sort_order?: number;
  is_published?: boolean;
}

export interface UpdateFAQRequest {
  slug?: string;
  category_id?: string;
  localized_content?: Record<string, LocalizedContent>;
  sort_order?: number;
  is_published?: boolean;
}

export interface CreateCategoryRequest {
  slug: string;
  localized_names: Record<string, string>;
  icon?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateCategoryRequest {
  slug?: string;
  localized_names?: Record<string, string>;
  icon?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface SetOverrideRequest {
  action: 'override' | 'exclude';
  override_content?: Record<string, LocalizedContent>;
}

// ============================================================================
// API Client
// ============================================================================

const faqClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
faqClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('ngui_auth_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============================================================================
// Public API (no auth required)
// ============================================================================

/**
 * List FAQ categories for current tenant.
 * @param lang Language code (e.g., 'en', 'it')
 */
export async function listCategories(lang: string = 'en'): Promise<FAQCategory[]> {
  const response = await faqClient.get<FAQCategory[]>('/faq/categories', {
    params: { lang }
  });
  return response.data;
}

/**
 * List FAQs for a category.
 * @param categorySlug Category slug
 * @param lang Language code
 */
export async function listFAQs(categorySlug: string, lang: string = 'en'): Promise<FAQ[]> {
  const response = await faqClient.get<FAQ[]>(`/faq/${categorySlug}`, {
    params: { lang }
  });
  return response.data;
}

/**
 * Get a specific FAQ by slug.
 * @param categorySlug Category slug
 * @param faqSlug FAQ slug
 * @param lang Language code
 */
export async function getFAQ(
  categorySlug: string,
  faqSlug: string,
  lang: string = 'en'
): Promise<FAQ> {
  const response = await faqClient.get<FAQ>(`/faq/${categorySlug}/${faqSlug}`, {
    params: { lang }
  });
  return response.data;
}

/**
 * Search FAQs.
 * @param query Search query
 * @param lang Language code
 * @param limit Max results
 */
export async function searchFAQs(
  query: string,
  lang: string = 'en',
  limit: number = 10
): Promise<FAQSearchResult[]> {
  const response = await faqClient.get<FAQSearchResult[]>('/faq/search', {
    params: { q: query, lang, limit }
  });
  return response.data;
}

// ============================================================================
// Admin API - Universal FAQs (superadmin only)
// ============================================================================

/**
 * List universal categories (admin).
 */
export async function listUniversalCategories(): Promise<AdminCategory[]> {
  const response = await faqClient.get<AdminCategory[]>('/admin/faq/universal/categories');
  return response.data;
}

/**
 * Create universal category.
 */
export async function createUniversalCategory(
  data: CreateCategoryRequest
): Promise<AdminCategory> {
  const response = await faqClient.post<AdminCategory>('/admin/faq/universal/categories', data);
  return response.data;
}

/**
 * Update universal category.
 */
export async function updateUniversalCategory(
  categoryId: string,
  data: UpdateCategoryRequest
): Promise<AdminCategory> {
  const response = await faqClient.put<AdminCategory>(
    `/admin/faq/universal/categories/${categoryId}`,
    data
  );
  return response.data;
}

/**
 * Delete universal category.
 */
export async function deleteUniversalCategory(categoryId: string): Promise<void> {
  await faqClient.delete(`/admin/faq/universal/categories/${categoryId}`);
}

/**
 * List universal FAQs (admin).
 */
export async function listUniversalFAQs(): Promise<AdminFAQ[]> {
  const response = await faqClient.get<AdminFAQ[]>('/admin/faq/universal');
  return response.data;
}

/**
 * Create universal FAQ.
 */
export async function createUniversalFAQ(data: CreateFAQRequest): Promise<AdminFAQ> {
  const response = await faqClient.post<AdminFAQ>('/admin/faq/universal', data);
  return response.data;
}

/**
 * Update universal FAQ.
 */
export async function updateUniversalFAQ(
  faqId: string,
  data: UpdateFAQRequest
): Promise<AdminFAQ> {
  const response = await faqClient.put<AdminFAQ>(`/admin/faq/universal/${faqId}`, data);
  return response.data;
}

/**
 * Delete universal FAQ.
 */
export async function deleteUniversalFAQ(faqId: string): Promise<void> {
  await faqClient.delete(`/admin/faq/universal/${faqId}`);
}

// ============================================================================
// Admin API - Tenant FAQs
// ============================================================================

/**
 * List tenant-specific FAQs.
 */
export async function listTenantFAQs(tenantId: string): Promise<AdminFAQ[]> {
  const response = await faqClient.get<AdminFAQ[]>(`/admin/faq/tenant/${tenantId}`);
  return response.data;
}

/**
 * Create tenant-specific FAQ.
 */
export async function createTenantFAQ(
  tenantId: string,
  data: CreateFAQRequest
): Promise<AdminFAQ> {
  const response = await faqClient.post<AdminFAQ>(`/admin/faq/tenant/${tenantId}`, data);
  return response.data;
}

/**
 * Update tenant-specific FAQ.
 */
export async function updateTenantFAQ(
  tenantId: string,
  faqId: string,
  data: UpdateFAQRequest
): Promise<AdminFAQ> {
  const response = await faqClient.put<AdminFAQ>(
    `/admin/faq/tenant/${tenantId}/${faqId}`,
    data
  );
  return response.data;
}

/**
 * Delete tenant-specific FAQ.
 */
export async function deleteTenantFAQ(tenantId: string, faqId: string): Promise<void> {
  await faqClient.delete(`/admin/faq/tenant/${tenantId}/${faqId}`);
}

// ============================================================================
// Admin API - Tenant Overrides
// ============================================================================

/**
 * List tenant's overrides/exclusions.
 */
export async function listTenantOverrides(tenantId: string): Promise<TenantOverride[]> {
  const response = await faqClient.get<TenantOverride[]>(
    `/admin/faq/tenant/${tenantId}/overrides`
  );
  return response.data;
}

/**
 * Set override or exclusion for a universal FAQ.
 */
export async function setTenantOverride(
  tenantId: string,
  faqSlug: string,
  data: SetOverrideRequest
): Promise<void> {
  await faqClient.post(`/admin/faq/tenant/${tenantId}/override/${faqSlug}`, data);
}

/**
 * Remove override for a universal FAQ.
 */
export async function removeTenantOverride(
  tenantId: string,
  faqSlug: string
): Promise<void> {
  await faqClient.delete(`/admin/faq/tenant/${tenantId}/override/${faqSlug}`);
}

// ============================================================================
// Export all as namespace
// ============================================================================

export const faqApi = {
  // Public
  listCategories,
  listFAQs,
  getFAQ,
  searchFAQs,

  // Admin - Universal
  listUniversalCategories,
  createUniversalCategory,
  updateUniversalCategory,
  deleteUniversalCategory,
  listUniversalFAQs,
  createUniversalFAQ,
  updateUniversalFAQ,
  deleteUniversalFAQ,

  // Admin - Tenant
  listTenantFAQs,
  createTenantFAQ,
  updateTenantFAQ,
  deleteTenantFAQ,

  // Admin - Overrides
  listTenantOverrides,
  setTenantOverride,
  removeTenantOverride,
};

export default faqApi;
